/**
 * Campaign-aware API binding.
 *
 * Each campaign targets its own backend module (separate endpoints), so the
 * generated react-query hooks differ per campaign. This module exposes a single
 * `campaignApi` object pointing to the hooks of the active campaign, so the form
 * components stay campaign-agnostic.
 */
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import { activeCampaignSlug } from './active'

/**
 * Loose mutation hook signature shared by every campaign.
 * Payloads differ slightly between products (quantity shape), so `data` is kept
 * generic; each form builds its own typed payload locally.
 */
export type CampaignMutationHook = (options?: {
  mutation?: UseMutationOptions<any, any, { data: any }, any>
}) => UseMutationResult<any, any, { data: any }, any>

/**
 * Set of mutation hooks used across the recall wizard for a single campaign.
 */
export interface CampaignApi {
  useCreateEmailConfirmation: CampaignMutationHook
  useCreateOwnerChanged: CampaignMutationHook
  useCreateUnknownNewOwner: CampaignMutationHook
  useCreateQuantity: CampaignMutationHook
  useCreateKitRequest: CampaignMutationHook
}

/**
 * Empty in this checkout, and not an oversight.
 *
 * These bindings point at hooks Orval generates from the backend's swagger, and
 * the SD500 module is `enabled: false` here (see backend `campaigns.config.ts`),
 * so its routes are absent from the spec and its hooks no longer exist. Binding
 * them would not compile.
 *
 * That is consistent rather than broken: this checkout cannot serve SD500 —
 * its endpoints return 404 on this backend — so a frontend built here could not
 * talk to them either. SD500 is built and deployed from its own checkout, where
 * the module is enabled and the hooks are generated.
 *
 * The Simond campaign does not appear here at all: it ships its own screens and
 * talks to the backend through `campaigns/simond-quickdraw/api.ts`, not through
 * the wizard's shared binding.
 */
const apiByCampaign: Record<string, CampaignApi> = {}

/**
 * Stand-in for campaigns with no backend binding.
 *
 * Throws on first use rather than at import time. Campaigns that ship their own
 * screens never touch these hooks, yet this module is still pulled in
 * transitively through the campaign registry — throwing on import would stop
 * them booting at all. Wizard campaigns still fail fast, just at the point of
 * use rather than at load, so a misconfigured CAMPAIGN can never silently fall
 * back to another campaign's endpoints.
 */
const missingCampaignApi = (): CampaignApi =>
  new Proxy({} as CampaignApi, {
    get(_target, property) {
      throw new Error(
        `Campaign "${activeCampaignSlug ?? 'undefined'}" has no API binding, but ` +
          `${String(property)} was used. Known bindings: ${Object.keys(apiByCampaign).join(', ')}`,
      )
    },
  })

/**
 * API hooks for the active campaign.
 */
export const campaignApi: CampaignApi =
  (activeCampaignSlug && apiByCampaign[activeCampaignSlug]) || missingCampaignApi()
